import React from "react"
import {
  CateringCheckoutInput,
  CateringContext,
  CateringPaymentMethod,
} from "./catering-context"
import { Restaurant } from "../../models/restaurant"
import moment from "moment"

type Props = {
  children: React.ReactNode
  restaurant?: Restaurant
}

export type ShopCateringSpec = {
  shopIds: string[]
  menuPageCateringDescription: string
  checkoutCateringDescription: string
  cateringOrderWindowStartInDays: number
  cateringOrderWindowEndInDays: number
  checkoutInputs: CateringCheckoutInput[]
  cateringPaymentMethods: CateringPaymentMethod[]
}

// Use this directly if logic outside of CateringProvider is needed (See triggerPayment in checkout.tsx).
export const getCateringSpec = (shopId: string | undefined) => {
  if (!shopId) {
    return undefined
  }
  return SHOP_CATERING_SPEC.find((spec) => spec.shopIds.includes(shopId))
}

// shopIds is an array of shop ids so that we can have same configuration for multiple shops.
export const SHOP_CATERING_SPEC: ShopCateringSpec[] = [
  {
    shopIds: [
      // Catering Bone's
      "0195dc0a-c1f4-725d-a0e7-e53df08add6b",
      "0195ffa6-d8dd-739f-a45a-0ebcc36b6fc3",
      "0195fbba-2893-7171-b43f-aa577144cc75",
      "0195fbc1-99fd-73e8-bd72-0a95143bbd34",
      "0195dc0a-c1f4-725d-a0e7-e53df08add6b",
      "0195fbc6-067b-71f2-b224-51f95ab04308",
      "0195fb73-daa2-72f9-80fa-0e33489273c7",
      "0195fbcb-d892-70aa-aade-bd8950390977",
      "0195fb69-689a-7002-b82d-63672dfa1d81",
      "0195ffaa-724a-738d-a77f-288385ecbd8f",
      "0195fb46-6959-71fe-bb4e-a88f454e6197",
      "01961458-048c-722c-8642-36856048c8aa",
    ],
    menuPageCateringDescription:
      "Bestil amerikansk BBQ-catering hos Bone’s\n\nInvitér Bone’s med som gæst og gør din fest mindeværdig og velsmagende. Oplev den autentiske smag af Bone’s Real American BBQ som catering. Vores omhyggeligt udvalgte menu byder bl.a. på vores møre spareribs, smagfulde Tritip, velsmagende oksefilet, lækre friske salater, flødekartofler, ovnbagte kartofler, koldrøget laks, delikat cheesecake og meget mere.\n\nVi gør det let for dig - lige fra bestilling til levering eller afhentning. Du vælger nemlig helt selv, hvor stor en del af festen vi skal være en del af. Uanset sørger vores professionelle team for, at din fest fyldes af smagfulde øjeblikke, så du kan nyde American BBQ, helt uden besvær.",
    checkoutCateringDescription:
      "Med Bone’s på menuen!\n\nDu er i gang med at bestille catering fra Bone’s, og vi glæder os til at levere lækre retter, møre ribs og masser af hygge til din fest. Skulle du få brug for at ændre noget undervejs, så skriv endelig til os på catering@bones.dk – vi står klar til at hjælpe!",
    cateringOrderWindowStartInDays: 7,
    cateringOrderWindowEndInDays: moment()
      .add(4, "months")
      .diff(moment(), "days"),
    checkoutInputs: [],
    cateringPaymentMethods: ["ean"],
  },
  {
    shopIds: [
      // Rå sushi staging & Grød Frederiksberg staging
      "950dfdd9-eb07-4263-a214-e32a78ba54cf",
      "c6a900be-123e-4274-b695-9aa33c26e29e",
      // Catering Madklubben
      "0195f11d-bdb3-70a9-8c26-bf46b3c9ef2e",
    ],
    menuPageCateringDescription:
      "Hanzōs catering menuer bliver tilberedt på restauranten på Østerbro. Du kan enten afhente din bestilling eller få den leveret.",
    checkoutCateringDescription:
      "Jeres valgte menu kommer anrettet til deling i engangsemballage. Ønsker I selv at anrette maden på jeres eget serveringsservice, er det let at overføre.",
    cateringOrderWindowStartInDays: 2,
    cateringOrderWindowEndInDays: moment("2025-09-23").diff(moment(), "days"),
    checkoutInputs: [
      {
        name: "catering_pickup_date",
        label: "Returnering af service",
        placeholder: "Skriv dato her",
        helperText:
          "Ved afhentning: Hvilken dato returnere i termokasser (senest 3 dage efter arrangementet). Ved levering: Hvilken dato kan vi afhente termokasser (senest 3 dage efter arrangementet). Vælg et tidsinterval på en time mellem kl. 10.00 og 16.00 alle dage, og skriv dette tidsinterval i kommentarfeltet, så kommer vi og henter termokasserne.",
      },
    ],
    cateringPaymentMethods: ["credit_card"],
  },
]

// For using in nested components, so we dont have to find the spec for a single shop every time.
export const CateringProvider = ({ children, restaurant }: Props) => {
  const shopCateringSpec = getCateringSpec(restaurant?.shop.id)

  return (
    <CateringContext.Provider
      value={{
        checkoutCateringDescription:
          shopCateringSpec?.checkoutCateringDescription || "",
        isCatering: !!shopCateringSpec,
        cateringOrderWindowStartInDays:
          shopCateringSpec?.cateringOrderWindowStartInDays || 0,
        cateringOrderWindowEndInDays:
          shopCateringSpec?.cateringOrderWindowEndInDays || 0,
        menuPageCateringDescription:
          shopCateringSpec?.menuPageCateringDescription || "",
        checkoutInputs: shopCateringSpec?.checkoutInputs || [],
        cateringPaymentMethods: shopCateringSpec?.cateringPaymentMethods || [],
      }}
    >
      {children}
    </CateringContext.Provider>
  )
}
